import { useCallback } from "react";
import Select from "components/ui/Select";
import { roundAmount2Dig } from "utils/amount";
import { useBill } from "hooks/bill";
import useAuth from "hooks/useAuth";
import Input from "components/ui/Input";

export default function AgentCommission({
  totalAmountAfterAllDiscounts,
  totalTax,
  tcsAmount,
  freightAfterTax,
  docType,
}) {
  const { agentCommission, updateAgentCommission } = useBill();
  const { agttp } = useAuth();

  const allCommTypeOptions = [
    { value: "SBT", label: "Sale bill amount before Tax" },
    { value: "SB", label: "Sale bill amount" },
  ];

  // Function to calculate amount based on commType and percentage
  // Matches PHP: sum_temp = totalAmountAfterAllDiscounts + totalTax + tcsAmount + freightAfterTax
  const calculateAmount = useCallback((percentage, commType) => {
    const baseAmount =
      commType === "SBT"
        ? totalAmountAfterAllDiscounts
        : totalAmountAfterAllDiscounts + totalTax + tcsAmount + freightAfterTax;

    return (baseAmount * percentage) / 100;
  }, [totalAmountAfterAllDiscounts, totalTax, tcsAmount, freightAfterTax]);

  // Handle change for commType selection
  const handleCommTypeChange = (val) => {
    updateAgentCommission({
      commType: val.value,
      amount: calculateAmount(agentCommission.percentage, val.value), // Recalculate the amount based on new commType
    });
  };

  // Handle change for percentage input
  const handlePercentageChange = (e) => {
    const percentage = parseFloat(e.target.value) || 0;
    updateAgentCommission({
      percentage: percentage,
      amount: calculateAmount(percentage, agentCommission.commType), // Recalculate the amount when percentage changes
    });
  };

  // Handle change for amount input
  const handleAmountChange = (e) => {
    const amount = parseFloat(e.target.value) || 0;
    updateAgentCommission({
      amount: amount,
    });
  };

  // Sales commission applies only to agent sale bills.
  if (agttp === 1 || docType !== "AGENTBILL") {
    return null;
  }

  return (
    <div className="w-full mt-4 border-t border-gray-300">
      <h1 className="mt-4 font-semibold leading-6 text-lg text-gray-900 flex items-center justify-center">
        Sales Commission
      </h1>
      <div className="w-full mt-4 border-t border-gray-300" />
      <div className="grid grid-cols-3">
        <div>
          <div className="mb-10">
            <Select
              label="Commission On"
              value={allCommTypeOptions.find(
                (option) => option.value === agentCommission.commType
              )}
              onChange={handleCommTypeChange}
              options={allCommTypeOptions}
              isClearable={false}
              isSearchable={false}
            />
          </div>
        </div>
        <div className="mx-2">
          <label
            htmlFor="percentage"
            className="block text-sm font-medium leading-6 text-gray-900"
          >
            Percentage
          </label>
          <div className="mt-2 px-2">
            <Input
              type="number"
              name="percentage"
              id="percentage"
              value={agentCommission.percentage}
              onChange={handlePercentageChange}
              autoComplete="percentage"
              className="w-1/3 mx-3"
            />
          </div>
        </div>
        <div className="">
          <label
            htmlFor="amount"
            className="block text-sm font-medium leading-6 text-gray-900"
          >
            Amount
          </label>
          <div className="mt-2">
            <Input
              type="number"
              name="amount"
              id="amount"
              value={roundAmount2Dig(agentCommission.amount)}
              onChange={handleAmountChange}
              autoComplete="amount"
              className="w-1/2 mx-3"
            />
          </div>
        </div>
      </div>
    </div>
  );
}
